// Focus the first usable form field inside a container.
// Used by the shared DialogComp wrappers (modals, automatic) and exposed as a
// hook/wrapper for non-modal page forms (opt-in). Safe by design: no-ops when
// there is nothing sensible to focus.
import React, { useEffect, useRef } from 'react';

// Fields we consider "the first field". Hidden inputs, disabled/readonly controls
// and the MUI Select's hidden native input (tabindex=-1) are excluded below.
const FIELD_SELECTOR =
    'input:not([type=hidden]):not([disabled]):not([readonly]),' +
    'textarea:not([disabled]):not([readonly]),' +
    'select:not([disabled]),' +
    '[contenteditable="true"]';

export const focusFirstDialogField = (node) => {
    try {
        if (!node || typeof node.querySelectorAll !== 'function') return;

        // If the dialog already put focus on one of its own fields (e.g. a form
        // that manages its own autoFocus), leave it alone.
        const active = document.activeElement;
        if (
            active &&
            node.contains(active) &&
            typeof active.matches === 'function' &&
            active.matches(FIELD_SELECTOR)
        ) {
            return;
        }

        const fields = node.querySelectorAll(FIELD_SELECTOR);
        for (let i = 0; i < fields.length; i++) {
            const el = fields[i];
            // Visible (offsetParent null => display:none / hidden) and focusable.
            if (el.offsetParent !== null && el.tabIndex !== -1) {
                el.focus();
                return;
            }
        }
    } catch (e) {
        // Never let focus handling break a dialog from opening.
    }
};

// Build the TransitionProps to spread onto a MUI Dialog: run our focus first,
// then defer to any onEntered the caller already passed (so their explicit
// focus wins).
export const withAutoFocusTransition = (transitionProps = {}) => ({
    ...transitionProps,
    onEntered: (nodeEl, isAppearing) => {
        focusFirstDialogField(nodeEl);
        if (typeof transitionProps.onEntered === 'function') {
            transitionProps.onEntered(nodeEl, isAppearing);
        }
    }
});

// ---------------------------------------------------------------------------
// Non-modal / page forms (OPT-IN)
//
// Modals are handled automatically by the DialogComp wrappers above. Page forms
// have no shared mount point, and focusing indiscriminately on every route would
// steal focus into search bars / grid filters, so page forms opt in with one line.
//
// Usage - attach the returned ref to the form's container element:
//     const focusRef = useAutoFocusFirstField();
//     return <Grid container ref={focusRef}> ... </Grid>;   // MUI Grid forwards ref
//
// Pass deps to re-focus when data arrives (e.g. after an edit-load):
//     const focusRef = useAutoFocusFirstField([loaded]);
// ---------------------------------------------------------------------------
export const useAutoFocusFirstField = (deps = []) => {
    const ref = useRef(null);
    useEffect(() => {
        // Defer a tick so children have mounted and any layout has settled.
        const t = setTimeout(() => focusFirstDialogField(ref.current), 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return ref;
};

// Convenience wrapper for forms that don't already have a ref-able container.
// Renders a plain <div> and focuses its first field on mount.
//     <AutoFocusFields> ...form fields... </AutoFocusFields>
export const AutoFocusFields = ({ children, deps = [], ...rest }) => {
    const ref = useAutoFocusFirstField(deps);
    return React.createElement('div', { ref, ...rest }, children);
};
